<template>
	<f7-page
		class="create-payment-view"
		name="create-payment"
		:page-content="false"
		@page:beforein="handleBeforeIn"
	>
		<template v-if="account && payment">
			<template v-if="payment.status !== BuyingOperationStatus.EXPIRED">
				<CreatePaymentNavbar
					:payment="payment"
					:timer-service="timerService"
					@back="handleBack"
				/>
				<div class="create-payment-view__body">
                    <transition name="fade" mode="in-out">
                        <div class="create-payment-view__error" v-if="isAgentBetweenRangeError">
                            <BlockWarning>
                                <template #message>
                                    <div class="create-payment-view__error-message">
                                        <template v-if="formatedAgentBetweenRanges.length">
                                            {{$t('light-payment.errors.10036-1')}}<span v-for="range in formatedAgentBetweenRanges" :key="range">{{ range }} </span>.{{$t('light-payment.errors.10036-2')}}
                                        </template>
                                        <template v-else>
                                            {{$t('light-payment.errors.unavailable')}}
                                        </template>
                                    </div>
                                </template>
                            </BlockWarning>
                        </div>
                    </transition>
					<div class="create-payment-view__content">
						<CreatePaymentInput />
						<CreatePaymentTabs />
					</div>
				</div>
				<div class="create-payment-view__footer">
					<f7-button
						class="theme-type"
						@click="handleNext"
						:loading="isNextButtonLoading"
						:disabled="isButtonDisabled"
						preloader
					>
						{{ t('light-payment.create.submit') }}
					</f7-button>
				</div>
			</template>
			<div v-else class="create-payment-view__expired">
				<PaymentStatusExpired
					always-timer
					:payment="payment"
					:timer-service="timerService"
					@return-to-site="handleBack"
				/>
			</div>
		</template>
		<LoaderBlock v-else />
	</f7-page>
</template>

<script setup lang="ts">
import CreatePaymentTabs from "./componetns/CreatePaymentTabs.vue";
import CreatePaymentInput from "./componetns/CreatePaymentInput.vue";
import CreatePaymentNavbar from "./componetns/CreatePaymentNavbar.vue";
import CreatePaymentViewController from "./ts/CreatePaymentViewController";
import {computed, ref, watch} from "vue";
import LoaderBlock from "@components/atomic/loader-block/LoaderBlock.vue";
import {BuyingOperationStatus} from "@enums/BuyingOperationStatus";
import PaymentStatusExpired from "@/targets/light/views/payment/components/contents/PaymentStatusExpired.vue";
import CreatePaymentErrorHandler from "@/targets/light/views/create-payment/ts/CreatePaymentErrorHandler";
import {useI18n} from "vue-i18n";
import BlockWarning from "@components/atomic/block-warning/BlockWarning.vue";
import UtilsService from "@/services/others/UtilsService";

const viewController = CreatePaymentViewController.of();
const errorHelper = CreatePaymentErrorHandler.of();

const {
	account,
    currency,
	payment,
	isNextButtonLoading,
	isInputAmountError,
	isPaymentFlowError,
	isAgentBetweenRangeError,
	agentBetweenRanges,
	paymentService,
} = viewController;

const timerService = paymentService.timerService;

const { t } = useI18n();

const isButtonDisabled = computed(() =>
	isInputAmountError.value ||
	isPaymentFlowError.value ||
	isNextButtonLoading.value ||
    isAgentBetweenRangeError.value
);

const utilsService = UtilsService.of();
const currencyFormatter = computed(() => utilsService.currencyFormatter(currency.value));

const formatedAgentBetweenRanges = computed(() => {
	const ranges = agentBetweenRanges.value || [];
	if (!ranges.length) return [];

	return ranges.map(({ min, max }, i) =>
		`${currencyFormatter.value.format(min)} - ${currencyFormatter.value.format(max)}${i === ranges.length - 1 ? '' : '; '}`
	);
});


const handleBeforeIn = async () => {
	await viewController.next();
}

const handleBack = () => {
	viewController.returnToSite();
}

const handleNext = async () => {
	try {
		await viewController.handleSubmit();
	} catch (e) {
		errorHelper.handle(e);
	}
}

watch(timerService.getPercent, (value) => {
	if (value <= 0) {
		paymentService.updatePayment();
	}
});

watch(payment, () => {
	if (payment.value?.status === BuyingOperationStatus.EXPIRED) {
		document.querySelector('.create-payment-navbar')?.remove();
	}

	checkAndRunTimerCallbackFunc();
});

const checkAndRunTimerCallbackFunc = () => {
	if (
		payment.value &&
		payment.value?.status &&
		[BuyingOperationStatus.EMPTY, BuyingOperationStatus.EXPIRED]
			.includes(payment.value?.status)
	) {
		paymentService.timerService.setCallback(paymentService.updatePayment.bind(paymentService));
	}
}
</script>

<style scoped lang="scss">
.create-payment-view {
	display: grid;
	grid-template-rows: 1fr auto;
	background-color: var(--white1);

	&__body {
		padding: 4.5rem 1rem;
		overflow-y: auto;
	}

    &__error {
        margin-bottom: 2rem;
    }

    &__error-message {
      span {
        font-weight: bold;
      }
    }

	&__expired {
		display: grid;
		align-items: center;
		padding: 4.5rem 1rem;
		overflow-y: auto;

		.payment__status.expired {
			margin-top: unset;
		}
	}

	&__content {
		display: flex;
		flex-direction: column;
		gap: 2rem;
	}

	&__footer {
		padding: 1rem;
		border-top: 1px solid #DBDDE4;
		background-color: var(--white1);
	}
}
</style>
